import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchUserDoc, fetchProfilesByIds } from "../../services/firestoreQueries";
import { getProfileImages } from "../../utils/profileLookup";

/**
 * Loads a list of profiles referenced by an array of IDs stored on the
 * current user document (e.g. blocked, shortlisted).
 * Returns normalised profile objects suitable for ProfileCard / ProfileList.
 * Uses batched queries and writes individual profiles into React Query cache
 * to avoid repeated single-document reads across the app.
 */
export default function useUserProfileList(user, field) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const userDoc = await fetchUserDoc(user.uid);
        const ids = userDoc[field] || [];

        if (!ids.length) {
          if (active) setItems([]);
          return;
        }

        const profilesMap = await fetchProfilesByIds(ids);
        const profiles = ids.map((id) => {
          const data = profilesMap[id];
          if (!data) return null;
          const normalized = {
            id,
            fullName: data.fullName || "Profile",
            dateOfBirth: data.dateOfBirth || data.dob || data.birthDate || "",
            gender: data.gender || "",
            caste: data.caste || "",
            profession: data.profession || data.jobTitle || "",
            education: data.education || data.highestEducation || "",
            country: data.country || "",
            state: data.state || "",
            presentResidence: data.presentResidence || "",
            workingPlace: data.workingPlace || "",
            kmProfileId: data.kmProfileId || "",
            hidePhotos: Boolean(data.hidePhotos),
            imageUrls: getProfileImages(data),
            membership: data.membership,
            profileStatus: data.profileStatus,
          };

          try {
            queryClient.setQueryData(["profile", id], normalized);
          } catch (e) {
            // ignore cache failures
          }

          return normalized;
        }).filter(Boolean);

        if (active) setItems(profiles);
      } catch (err) {
        if (active) setError(err.message || "Could not load profiles.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [user, field, queryClient]);

  return { items, setItems, loading, error, setError };
}
